import { useMemo, useState } from "react";
import { Plus } from "lucide-react";

import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import { DateRangePicker, toIsoDate, type DateRange } from "@/components/cinch/DatePicker";
import {
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
  MultiSelectChip,
} from "@/components/cinch/FilterBar";
import { LinkOut } from "@/components/cinch/LinkOut";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  CARRIER_LABEL,
  SHIPMENT_LABEL,
  fmtDate,
  fmtMoney,
  useCancelRequest,
  useDecideRequest,
  useDepartments,
  useItems,
  useLedger,
  useRequests,
  type Disbursement,
  type SwagRequest,
} from "../api";
import { RequestDialog } from "./RequestDialog";

/** Sentinel for "no department" — a filter value, never a department id. */
const UNASSIGNED = "unassigned";

/** Shipping states worth filtering by. "Handed over" is first because it is
 *  the most common answer and the one people forget is a state at all. */
const SHIPPING_FILTERS = [
  { value: "none", label: "Handed over (no box)" },
  { value: "pending", label: "Pending" },
  { value: "label_created", label: "Label created" },
  { value: "in_transit", label: "In transit" },
  { value: "delivered", label: "Delivered" },
  { value: "exception", label: "Exception" },
];

/**
 * Detail tab — the accounting, row by row, plus the request queue.
 *
 * The ledger is deliberately the whole company's view: the reason it exists is
 * so anyone about to gift a customer can check whether that person already has
 * one. Requests sit above it because a pending ask is the thing that needs a
 * human; the ledger below is reference.
 */
export function DetailView({
  canRequest,
  isAdmin,
}: {
  canRequest: boolean;
  isAdmin: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [deptIds, setDeptIds] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [flags, setFlags] = useState<string[]>([]);
  const [dates, setDates] = useState<DateRange>({ start: null, end: null });
  const [search, setSearch] = useState("");
  // Newest-first until a header says otherwise — `null` means "the default",
  // which is not the same as sorting by use_date descending: the default also
  // breaks ties on when the row was entered.
  const [sort, setSort] = useState<DataTableSort | null>(null);

  const items = useItems();
  const departments = useDepartments();
  const requests = useRequests();
  // "Unassigned" is a filter value, not a department id, so it travels in its
  // own flag — those are the rows Cody can't post, and they have to be findable.
  const unassigned = deptIds.includes(UNASSIGNED);
  const ledger = useLedger({
    item_ids: itemIds,
    department_ids: deptIds.filter((d) => d !== UNASSIGNED),
    include_unassigned: unassigned,
    shipment_states: states,
    q: search || null,
    needs_review: flags.includes("review") ? true : null,
    date_from: dates.start ? toIsoDate(dates.start) : null,
    date_to: dates.end ? toIsoDate(dates.end) : null,
    sort: sort?.key ?? null,
    direction: sort?.dir ?? "desc",
    limit: 500,
  });

  const openRequests = useMemo(
    () => (requests.data ?? []).filter((r) => r.status === "pending"),
    [requests.data],
  );
  const decidedRequests = useMemo(
    () => (requests.data ?? []).filter((r) => r.status !== "pending").slice(0, 10),
    [requests.data],
  );
  const flagged = (ledger.data?.rows ?? []).filter((r) => r.needs_review).length;

  const hasFilters =
    Boolean(search) ||
    itemIds.length > 0 ||
    deptIds.length > 0 ||
    states.length > 0 ||
    flags.length > 0 ||
    Boolean(dates.start || dates.end);

  const clearAll = () => {
    setSearch("");
    setItemIds([]);
    setDeptIds([]);
    setStates([]);
    setFlags([]);
    setDates({ start: null, end: null });
  };

  const columns: DataTableColumn<Disbursement>[] = [
    {
      key: "use_date",
      header: "Date",
      sortable: true,
      render: (row) => fmtDate(row.use_date),
    },
    {
      key: "item_name",
      header: "Item",
      sortable: true,
      render: (row) => (
        <span className="font-medium">
          {row.item_name}
          {row.quantity > 1 && (
            <span className="text-muted-foreground"> ×{row.quantity}</span>
          )}
        </span>
      ),
    },
    {
      key: "recipient_name",
      header: "Given to",
      sortable: true,
      render: (row) =>
        row.recipient_kind === "group" ? (
          <span className="text-muted-foreground">
            {row.recipient_name ?? row.occasion ?? "Group giveaway"}
            <StatusBadge value="group" className="ml-2" />
          </span>
        ) : (
          (row.recipient_name ?? "—")
        ),
    },
    { key: "recipient_company_name", header: "Company", sortable: true },
    { key: "employee_name", header: "Given by", sortable: true },
    {
      key: "department_name",
      header: "Department",
      sortable: true,
      render: (row) =>
        row.department_name ? (
          row.department_name
        ) : (
          <StatusBadge value="no_department" />
        ),
    },
    {
      key: "swag_cost",
      header: "Swag $",
      numeric: true,
      align: "right",
      sortable: true,
      render: (row) => fmtMoney(row.swag_cost),
    },
    {
      key: "shipping_cost_share",
      header: "Ship $",
      numeric: true,
      align: "right",
      sortable: true,
      render: (row) =>
        row.shipment_id ? fmtMoney(row.shipping_cost_share) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "shipment_status",
      header: "Shipping",
      sortable: true,
      render: (row) =>
        row.shipment_status ? (
          <div className="flex flex-col gap-1">
            <StatusBadge
              value={row.shipment_status}
              label={SHIPMENT_LABEL[row.shipment_status]}
            />
            {row.tracking_number && (
              <LinkOut href={row.tracking_url} size="xs">
                {row.carrier ? CARRIER_LABEL[row.carrier] : "Track"}
              </LinkOut>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Handed over</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {(openRequests.length > 0 || canRequest) && (
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">
              Requests{openRequests.length > 0 && ` · ${openRequests.length} awaiting approval`}
            </h2>
            {canRequest && (
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> Request swag
              </Button>
            )}
          </div>
          {openRequests.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing waiting on a decision.
            </p>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {openRequests.map((req) => (
                <RequestCard key={req.id} request={req} isAdmin={isAdmin} />
              ))}
            </div>
          )}
          {decidedRequests.length > 0 && (
            <CollapsibleCard
              title="Recently decided"
              meta={String(decidedRequests.length)}
              bare
            >
              <div className="grid gap-3 lg:grid-cols-2">
                {decidedRequests.map((req) => (
                  <RequestCard key={req.id} request={req} isAdmin={isAdmin} />
                ))}
              </div>
            </CollapsibleCard>
          )}
        </section>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Giveaway ledger</h2>
          <p className="text-xs text-muted-foreground">
            {ledger.data?.count ?? 0} row
            {(ledger.data?.count ?? 0) === 1 ? "" : "s"}
            {hasFilters && " matching"}
          </p>
        </div>

        <FilterBar>
          <FilterSearch
            value={search}
            onChange={setSearch}
            placeholder="Search person, company, or who gave it…"
          />
          <MultiSelectChip
            label="Item"
            options={(items.data ?? []).map((i) => ({ value: i.id, label: i.name }))}
            value={itemIds}
            onChange={setItemIds}
          />
          <MultiSelectChip
            label="Department"
            options={[
              ...(departments.data ?? []).map((d) => ({ value: d.id, label: d.name })),
              { value: UNASSIGNED, label: "Unassigned" },
            ]}
            value={deptIds}
            onChange={setDeptIds}
          />
          <MultiSelectChip
            label="Shipping"
            options={SHIPPING_FILTERS}
            value={states}
            onChange={setStates}
          />
          <FilterChip
            label="Given out"
            valueLabel={
              dates.start || dates.end
                ? `${dates.start ? toIsoDate(dates.start) : "…"} → ${
                    dates.end ? toIsoDate(dates.end) : "…"
                  }`
                : undefined
            }
          >
            <div className="space-y-2">
              <DateRangePicker value={dates} onChange={setDates} />
              {(dates.start || dates.end) && (
                <button
                  type="button"
                  onClick={() => setDates({ start: null, end: null })}
                  className="text-xs text-brand hover:underline focus-visible:outline-none focus-visible:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </FilterChip>
          {flagged > 0 && (
            <MultiSelectChip
              label="Flags"
              options={[
                { value: "review", label: "Needs review", count: flagged },
              ]}
              value={flags}
              onChange={setFlags}
            />
          )}
          <div className="flex-1" />
          {hasFilters && <FilterClearAll onClick={clearAll} />}
        </FilterBar>

        {flags.includes("review") && (
          <Alert variant="info">
            Rows imported from the spreadsheet with something missing or
            ambiguous — a typo’d address, no named recipient, a department we
            couldn’t match. They were imported exactly as written rather than
            guessed at; fix or clear the flag as you go.
          </Alert>
        )}

        <DataTable
          columns={columns}
          rows={ledger.data?.rows ?? []}
          rowKey={(row) => row.id}
          sort={sort}
          onSortChange={setSort}
          loading={ledger.isLoading}
          error={ledger.error ? String(ledger.error) : null}
          emptyMessage="No giveaways recorded yet."
          footer={() => (
            <tr className="border-t font-medium">
              <td className="px-3 py-2" colSpan={6}>
                {ledger.data?.total_units ?? 0} unit
                {(ledger.data?.total_units ?? 0) === 1 ? "" : "s"} ·{" "}
                {ledger.data?.count ?? 0} row
                {(ledger.data?.count ?? 0) === 1 ? "" : "s"}
              </td>
              <td className="px-3 py-2 text-right">
                {fmtMoney(ledger.data?.total_swag_cost)}
              </td>
              <td className="px-3 py-2 text-right">
                {fmtMoney(ledger.data?.total_shipping_cost)}
              </td>
              <td />
            </tr>
          )}
        />
      </section>

      {canRequest && (
        <RequestDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </div>
  );
}

function RequestCard({
  request,
  isAdmin,
}: {
  request: SwagRequest;
  isAdmin: boolean;
}) {
  const decide = useDecideRequest();
  const cancel = useCancelRequest();
  const pending = request.status === "pending";

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">
            {request.requester_name}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {request.total_units} unit{request.total_units === 1 ? "" : "s"}
            </span>
          </CardTitle>
          <StatusBadge value={request.status} />
        </div>
        <p className="text-xs text-muted-foreground">
          {request.department_name ?? "No department"} ·{" "}
          {fmtDate(request.submitted_at)}
          {request.needs_shipping && " · needs shipping"}
          {request.needed_by && ` · needed by ${fmtDate(request.needed_by)}`}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pb-4 text-sm">
        {request.purpose && <p>{request.purpose}</p>}
        <ul className="flex flex-col gap-1">
          {request.lines.map((line) => (
            <li key={line.id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">
                {line.item_name}
                {line.quantity > 1 && ` ×${line.quantity}`}
              </span>
              <span className="text-muted-foreground">
                →{" "}
                {line.recipient_kind === "group"
                  ? (line.occasion ?? "group giveaway")
                  : [line.recipient_name, line.recipient_company_name]
                      .filter(Boolean)
                      .join(" @ ")}
              </span>
            </li>
          ))}
        </ul>
        {request.decision_note && (
          <p className="text-xs text-muted-foreground">
            {request.decided_by_name}: “{request.decision_note}”
          </p>
        )}
        {pending && (
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <>
                <Button
                  size="sm"
                  disabled={decide.isPending}
                  onClick={() =>
                    decide.mutate({ id: request.id, approve: true })
                  }
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() =>
                    decide.mutate({ id: request.id, approve: false })
                  }
                >
                  Deny
                </Button>
              </>
            )}
            {request.can_cancel && (
              <Button
                size="sm"
                variant="ghost"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(request.id)}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
        {decide.error && (
          <Alert variant="danger">{String(decide.error)}</Alert>
        )}
      </CardContent>
    </Card>
  );
}
